import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  useEffect(() => {
  if (open) {
    setName(user?.name || "");
    setProfileError("");
  }
}, [open, user]);
  const [name, setName] = useState("");
const [profileBusy, setProfileBusy] = useState(false);
const [profileError, setProfileError] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
const saveProfile = async (e) => {
  e.preventDefault();

  const clean = name.trim();

  if (clean.length < 2) {
    setProfileError("O nome deve ter pelo menos 2 caracteres.");
    return;
  }

  setProfileError("");
  setProfileBusy(true);

  try {
    const response = await api.patch("/auth/me", {
  name: clean,
});

setName(response.data.name || clean);

toast.success("Perfil atualizado com sucesso");
  } catch (err) {
    const msg = formatApiError(err);
    setProfileError(msg);
    toast.error(msg);
  } finally {
    setProfileBusy(false);
  }
};
  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
    setShow(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) return setError("A nova senha deve ter ao menos 6 caracteres.");
    if (next !== confirm) return setError("A confirmação não coincide com a nova senha.");
    if (next === current) return setError("A nova senha deve ser diferente da atual.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Senha alterada com sucesso");
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-full max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6 bg-[#0B132B] border-slate-800 text-slate-100"
        data-testid="settings-dialog"
      >
        <DialogHeader className="sticky top-0 z-50 bg-[#0B132B] pb-4">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <KeyRound className="w-5 h-5 text-blue-400" /> Minha conta
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Gerencie seu perfil e as configurações de segurança da sua conta.
          </DialogDescription>
        </DialogHeader>
<div className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
  <div>
    <h3 className="text-sm font-semibold text-slate-200">
      Perfil
    </h3>
    <p className="text-xs text-slate-500 mt-1">
      Atualize seus dados pessoais.
    </p>
  </div>

  <form onSubmit={saveProfile} className="space-y-3">
    <div className="space-y-1.5">
      <Label htmlFor="profile-name" className="text-slate-300">
        Nome
      </Label>

      <Input
        id="profile-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-slate-900/60 border-slate-700 text-slate-100"
        placeholder="Seu nome"
        minLength={2}
        required
      />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor="profile-email" className="text-slate-300">
        E-mail
      </Label>

      <Input
        id="profile-email"
        value={user?.email || ""}
        readOnly
        className="bg-slate-900/40 border-slate-700 text-slate-400 cursor-not-allowed"
      />
    </div>

    {profileError && (
      <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">
        {profileError}
      </div>
    )}

    <Button
      type="submit"
      disabled={profileBusy}
      className="bg-blue-600 hover:bg-blue-500 text-white"
    >
      {profileBusy ? "Salvando..." : "Salvar perfil"}
    </Button>
  </form>
</div>
        <div className="pt-2">
  <h3 className="text-sm font-semibold text-slate-200">
    Alterar senha
  </h3>
  <p className="text-xs text-slate-500 mt-1">
    Troque sua senha periodicamente para manter a conta segura.
  </p>
</div>
        <form onSubmit={submit} className="space-y-4">
          <div className="glass-card p-3 flex items-start gap-2 text-xs text-slate-300">
            <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
            Use pelo menos 6 caracteres, combinando letras, números e símbolos. A senha nunca é
            armazenada em texto puro — ela é protegida com bcrypt.
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300">Senha atual</Label>
            <div className="relative">
              <Input
                required
                type={show ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 h-11 pr-9"
                data-testid="pwd-current"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                onClick={() => setShow((s) => !s)}
                aria-label="Alternar exibição"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Nova senha</Label>
              <Input
                required
                type={show ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 h-11"
                data-testid="pwd-new"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Confirmar</Label>
              <Input
                required
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 h-11"
                data-testid="pwd-confirm"
              />
            </div>
          </div>

          {error && (
            <div
              className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2"
              data-testid="pwd-error"
            >
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => onOpenChange(false)}
              data-testid="pwd-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="pwd-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar senha"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
